/**
 * Rückfrage bei Doppelbelegung (Benjamin, 03.10.2026): „Der User wird gefragt,
 * wie das passieren soll." Ein Blatt je Anfrage, darin je Konflikt die
 * passenden Lösungen; vorgewählt ist, den kürzeren Termin aus dem längeren
 * auszuklammern. Logik und Texte: lib/terminKonflikt.ts.
 *
 * Sperrzeiten der Inhaber („Nicht da", 16.10.2026) kommen als eigene Konflikte
 * (`sperre`) und haben zusätzlich „Trotzdem einplanen".
 *
 * Angeboten wird nur, was die Zeit wirklich aufteilt (04.10.2026: „doppelt
 * einplanen ist keine Option, genau so wenig wie nicht einplanen — ich möchte
 * ja gerade einplanen"). Wer keine Lösung will, geht zurück und ändert die Zeit.
 */
import { useCallback, useState } from "react";

import {
  aufteilungen, standardWahl, tagUndZeit, wahlText, type Konflikt, type KonfliktAntwort, type KonfliktArt,
} from "../../lib/terminKonflikt";
import "../../styles/konflikt.css";

type Offen = { konflikte: Konflikt[]; fertig: (a: KonfliktAntwort | null) => void };

export function useKonfliktFrage() {
  const [offen, setOffen] = useState<Offen | null>(null);
  const fragen = useCallback(
    (konflikte: Konflikt[]) => new Promise<KonfliktAntwort | null>((fertig) => setOffen({ konflikte, fertig })),
    [],
  );
  const dialog = offen ? (
    <KonfliktDialog
      konflikte={offen.konflikte}
      onFertig={(a) => { offen.fertig(a); setOffen(null); }}
    />
  ) : null;
  return { dialog, fragen };
}

function KonfliktDialog({ konflikte, onFertig }: {
  konflikte: Konflikt[]; onFertig: (a: KonfliktAntwort | null) => void;
}) {
  const [wahl, setWahl] = useState<Record<string, KonfliktArt>>(() =>
    Object.fromEntries(konflikte.map((k) => [k.slot_id, standardWahl(k)])));
  // Ohne Aufteilung (der neue Termin deckt den alten ganz) bleibt nur: zurück und Zeit ändern.
  const loesbar = konflikte.every((k) => aufteilungen(k).length > 0);

  const los = () => onFertig({ loesungen: konflikte.map((k) => ({ slot_id: k.slot_id, art: wahl[k.slot_id] })), ohne: [] });

  return (
    <div className="kf-schleier" onMouseDown={(e) => { if (e.target === e.currentTarget) onFertig(null); }}>
      <section className="kf-blatt" role="dialog" aria-modal="true" aria-labelledby="kf-titel">
        <header className="kf-kopf">
          <h3 id="kf-titel">{konflikte.every((k) => k.sperre) ? "Nicht da" : konflikte.length === 1 ? "Schon eingeplant" : `${konflikte.length} Überschneidungen`}</h3>
          <button type="button" className="kf-zu" aria-label="Zurück" onClick={() => onFertig(null)}>×</button>
        </header>
        <div className="kf-inhalt">
          {konflikte.map((k) => {
            const arten = aufteilungen(k);
            return (
              <fieldset key={k.slot_id} className="kf-konflikt">
                <legend>
                  {k.sperre ? (
                    <><strong>{k.person_name || "Jemand"}</strong> ist nicht da{k.bezeichnung && k.bezeichnung !== "nicht da" ? ` (${k.bezeichnung})` : ""}</>
                  ) : (
                    <><strong>{k.person_name || "Jemand"}</strong> ist in „{k.bezeichnung || "einem anderen Termin"}“</>
                  )}
                  <span className="kf-zeit"> · {tagUndZeit(k)}</span>
                </legend>
                {arten.length === 0 && (
                  <p className="kf-hinweis">Der neue Termin deckt diesen ganz — hier lässt sich nichts aufteilen. Geh zurück und wähle eine andere Zeit.</p>
                )}
                {arten.map((a) => (
                  <label key={a} className={"kf-wahl" + (wahl[k.slot_id] === a ? " ist-an" : "")}>
                    <input type="radio" name={`kf-${k.slot_id}`} checked={wahl[k.slot_id] === a}
                      onChange={() => setWahl({ ...wahl, [k.slot_id]: a })} />
                    <span>{wahlText(k, a)}</span>
                  </label>
                ))}
              </fieldset>
            );
          })}
        </div>
        <footer className="kf-fuss">
          <button type="button" className="vg-btn vg-btn-ghost" onClick={() => onFertig(null)}>‹ Zurück</button>
          {loesbar && <button type="button" className="vg-btn vg-btn-primary" onClick={los}>So eintragen</button>}
        </footer>
      </section>
    </div>
  );
}
